import React, { useCallback, useEffect, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Spinner,
    Input,
    FormControl,
    FormLabel,
    Grid,
    Divider,
    useToast,
} from '@chakra-ui/react';
import { LuGlobe, LuClock, LuUsers } from 'react-icons/lu';
import { fetchWrapper } from '../../../helpers';
import {
    zonedTimeToUtc,
    validateZonedSchedule,
    formatInZone,
    nowInZone,
    zoneOffsetLabel,
    NO_TIMEZONE,
} from '../../../utils/timezoneSchedule';

/** "No/Tz" is the API's bucket for contacts it could not resolve a real zone for. */
const timezoneDisplayLabel = (timezone) =>
    timezone === NO_TIMEZONE ? 'No Timezone' : timezone;

/** Pill-shaped chip — used instead of badges/tags throughout this modal. */
const Chip = ({ children, tone = 'gray', icon: IconCmp, ...rest }) => {
    const tones = {
        gray: { bg: 'gray.100', color: 'gray.700' },
        purple: { bg: 'purple.50', color: 'purple.700' },
        blue: { bg: 'blue.50', color: 'blue.700' },
        green: { bg: 'green.50', color: 'green.700' },
        red: { bg: 'red.50', color: 'red.600' },
    };
    const { bg, color } = tones[tone] || tones.gray;
    return (
        <HStack
            as="span"
            display="inline-flex"
            spacing={1}
            bg={bg}
            color={color}
            px={2.5}
            py={0.5}
            borderRadius="full"
            fontSize="11px"
            fontWeight="500"
            lineHeight="1.6"
            whiteSpace="nowrap"
            {...rest}
        >
            {IconCmp && <Box as={IconCmp} boxSize="11px" />}
            <span>{children}</span>
        </HStack>
    );
};

/**
 * Timezone list + reschedule for a campaign row.
 *
 * mode 'view'       — read-only list of the timezones a timezone_based campaign fires in
 * mode 'reschedule' — same list, plus a local date/time to move the schedule to
 *
 * A timezone_based campaign keeps ONE campaign_time and fans it out per timezone as
 * waves, so the time is edited once here rather than per row.
 */
const CampaignScheduleModal = ({ campaign, mode = 'view', isOpen, onClose, onSaved }) => {
    const toast = useToast();
    const isTimezoneBased = campaign?.timezone_based === 'Y';
    const canEdit = mode === 'reschedule';

    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');

    // The zone a non-timezone_based campaign is scheduled in; per-timezone campaigns
    // store 'all' or their first wave's zone, so fall back to the viewer's zone.
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const baseZone = (() => {
        const tz = campaign?.timezone;
        if (!tz || tz === 'all') return viewerZone;
        try {
            nowInZone(tz);
            return tz;
        } catch (e) {
            return viewerZone;
        }
    })();

    // GET /campaigns/time-zone-list returns the timezones this campaign sends to.
    // Shape varies, so accept a bare list of names or objects under the usual wrappers.
    const normalize = (res) => {
        const raw =
            res?.data?.timezones ||
            res?.data?.time_zone_list ||
            res?.data?.contact_timezone ||
            (Array.isArray(res?.data) ? res.data : null) ||
            (Array.isArray(res) ? res : []);

        const seen = new Set();
        return (raw || [])
            .map((item) => {
                if (typeof item === 'string') return { timezone: item };
                const timezone = item?.timezone || item?.time_zone || item?.tz || item?.name;
                if (!timezone) return null;
                return {
                    timezone,
                    // local_datetime is wall-clock time in that zone. It arrives with a
                    // 'Z' suffix ("2026-08-28T16:31:00.000Z") because the DB DATETIME is
                    // serialised as if UTC — so keep only the date+time part and resolve
                    // it against the zone. Reading it as a real instant would shift every
                    // row by its offset (16:31 Karachi would show as 21:31).
                    datetime:
                        item?.local_datetime ||
                        item?.datetime ||
                        item?.scheduled_at ||
                        item?.send_at ||
                        item?.scheduled_time ||
                        null,
                    status: item?.status || null,
                    count: item?.total_audience ?? item?.count ?? item?.audience_count ?? null,
                    sent: item?.total_sent ?? item?.sent ?? null,
                    // 1 = a real timezone resolved from the audience; 0 = this row was the
                    // "No Timezone" bucket, whether or not it was later assigned a zone.
                    availableTz: item?.available_tz ?? null,
                };
            })
            .filter((item) => {
                if (!item || seen.has(item.timezone)) return false;
                seen.add(item.timezone);
                try {
                    nowInZone(item.timezone);
                    return true;
                } catch (e) {
                    return false;
                }
            });
    };

    const loadZones = useCallback(async () => {
        if (!campaign?.id) return;
        setLoading(true);
        setError('');
        try {
            const url = `${process.env.REACT_APP_API_URL}/campaigns/time-zone-list?campaign_id=${campaign.id}`;
            const res = await fetchWrapper.get(url);
            const list = normalize(res);
            setZones(list);
            if (!list.length) setError('No timezones returned for this campaign.');
        } catch (err) {
            console.error('Failed to load campaign timezones', err);
            setZones([]);
            setError(err?.message || 'Could not load the timezone list.');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign?.id]);

    useEffect(() => {
        if (!isOpen) return;
        // Seed the inputs from the campaign's own schedule: campaign_time is the local
        // send time for per-timezone campaigns, otherwise read scheduled_at in its zone.
        const savedTime = String(campaign?.campaign_time || '').slice(0, 5);
        if (campaign?.scheduled_at) {
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: baseZone,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: false,
            })
                .formatToParts(new Date(campaign.scheduled_at))
                .reduce((acc, p) => (p.type === 'literal' ? acc : { ...acc, [p.type]: p.value }), {});
            setDate(`${parts.year}-${parts.month}-${parts.day}`);
            setTime(savedTime || `${parts.hour}:${parts.minute}`);
        } else {
            setDate('');
            setTime(savedTime);
        }
        if (isTimezoneBased) loadZones();
        else setZones([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, campaign?.id]);

    // Rows with the schedule applied: every zone reads the same local time. Sorted by
    // the instant they fire, so the list reads in wave order like the backend sends.
    const rows = zones
        .map((z) => {
            if (canEdit || !z.datetime) {
                return { ...z, ...validateZonedSchedule(date, time, z.timezone) };
            }
            // saved wall-clock time -> the instant it actually fires in that zone
            const [d = '', t = ''] = String(z.datetime).replace('Z', '').split('T');
            const instant = zonedTimeToUtc(d, t.slice(0, 5), z.timezone);
            return { ...z, valid: !!instant, instant, reason: instant ? '' : 'Invalid saved time' };
        })
        .sort((a, b) => (a.instant?.getTime() || 0) - (b.instant?.getTime() || 0));

    const invalidCount = canEdit ? rows.filter((r) => !r.valid).length : 0;
    const singleCheck = !isTimezoneBased && canEdit ? validateZonedSchedule(date, time, baseZone) : null;
    const totalAudience = rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0);
    const blocked = canEdit && (
        loading || !date || !time ||
        (isTimezoneBased ? (!rows.length || invalidCount > 0) : singleCheck?.valid === false)
    );

    const statusTone = (status) => {
        const s = String(status || '').toLowerCase();
        if (s === 'completed' || s === 'sent') return 'green';
        if (s === 'running') return 'blue';
        if (s === 'failed' || s === 'cancelled') return 'red';
        return 'gray';
    };

    const handleSave = async () => {
        if (blocked) return;
        setSaving(true);
        try {
            let body;
            if (isTimezoneBased) {
                const instants = rows.map((r) => r.instant).filter(Boolean);
                body = {
                    launch_type: 'scheduled',
                    timezone_based: 'Y',
                    timezone: 'all',
                    campaign_time: time,
                    scheduled_at: instants.length
                        ? new Date(Math.min(...instants.map((d) => d.getTime()))).toISOString()
                        : null,
                    contact_timezone: rows.map((r) => ({
                        timezone: r.timezone,
                        datetime: `${date}T${time}:00`,
                    })),
                };
            } else {
                const instant = zonedTimeToUtc(date, time, baseZone);
                body = {
                    launch_type: 'scheduled',
                    timezone_based: 'N',
                    timezone: baseZone,
                    campaign_time: time,
                    scheduled_at: instant ? instant.toISOString() : null,
                };
            }

            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaign.id}`;
            const res = await fetchWrapper.put(url, body);
            if (res?.success === false) throw new Error(res?.message || 'Reschedule failed');

            toast({
                title: 'Campaign rescheduled',
                description: isTimezoneBased
                    ? `Now sends at ${time} local time in ${rows.length} timezone${rows.length === 1 ? '' : 's'}.`
                    : `Now sends ${formatInZone(zonedTimeToUtc(date, time, baseZone), baseZone)} (${baseZone}).`,
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error('Reschedule failed', err);
            toast({
                title: 'Reschedule failed',
                description: err?.message || 'Could not update the schedule. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const localTimeLabel = String(campaign?.campaign_time || time || '').slice(0, 5);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={isTimezoneBased ? '2xl' : 'md'} scrollBehavior="inside" isCentered>
            <ModalOverlay bg="blackAlpha.400" />
            <ModalContent borderRadius="xl" overflow="hidden">
                <ModalHeader pb={3} pt={5}>
                    <Text fontSize="md" fontWeight="semibold" mb={2}>
                        {canEdit ? 'Reschedule campaign' : 'Timezone schedule'}
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                        <Chip>{campaign?.name || `Campaign ${campaign?.id}`}</Chip>
                        {isTimezoneBased ? (
                            <Chip tone="purple" icon={LuGlobe}>Per timezone</Chip>
                        ) : (
                            <Chip tone="blue" icon={LuClock}>Single send time</Chip>
                        )}
                        {/* {localTimeLabel && <Chip icon={LuClock}>{localTimeLabel} local</Chip>} */}
                        {isTimezoneBased && !loading && !error && rows.length > 0 && (
                            <>
                                <Chip icon={LuGlobe}>{rows.length} zone{rows.length === 1 ? '' : 's'}</Chip>
                                {totalAudience > 0 && (
                                    <Chip icon={LuUsers}>{totalAudience.toLocaleString()} contacts</Chip>
                                )}
                            </>
                        )}
                    </HStack>
                </ModalHeader>
                <ModalCloseButton top={4} />
                <Divider />

                <ModalBody py={4}>
                    {canEdit && (
                        <Box bg="gray.50" borderRadius="lg" p={3} mb={4}>
                            <Grid templateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={3}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="xs" color="gray.600" mb={1}>
                                        {isTimezoneBased ? 'Date (local)' : 'Date'}
                                    </FormLabel>
                                    <Input size="sm" bg="white" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize="xs" color="gray.600" mb={1}>
                                        {isTimezoneBased ? 'Time (local)' : 'Time'}
                                    </FormLabel>
                                    <Input size="sm" bg="white" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Text fontSize="xs" color="gray.600" mt={2}>
                                {isTimezoneBased
                                    ? 'Used as local time in every timezone below — each fires when its own clock reaches it.'
                                    : `Read in ${baseZone} (${zoneOffsetLabel(baseZone)}).`}
                            </Text>
                            {singleCheck && (
                                <Text fontSize="xs" mt={1} color={singleCheck.valid ? 'gray.700' : 'red.500'} fontWeight="500">
                                    {singleCheck.valid ? `Sends ${formatInZone(singleCheck.instant, baseZone)}` : singleCheck.reason}
                                </Text>
                            )}
                        </Box>
                    )}

                    {isTimezoneBased && (
                        loading ? (
                            <HStack fontSize="sm" color="gray.600" py={6} justify="center">
                                <Spinner size="sm" />
                                <Text>Loading timezones…</Text>
                            </HStack>
                        ) : error ? (
                            <VStack py={6} spacing={3}>
                                <Text fontSize="sm" color="red.500" textAlign="center">{error}</Text>
                                <Button size="xs" variant="outline" onClick={loadZones}>Retry</Button>
                            </VStack>
                        ) : (
                            <VStack align="stretch" spacing={0} borderWidth="1px" borderColor="gray.100" borderRadius="lg" overflow="hidden">
                                {rows.map((r, i) => (
                                    <Flex
                                        key={r.timezone}
                                        align="center"
                                        justify="space-between"
                                        gap={3}
                                        px={4}
                                        py={3}
                                        borderTop={i === 0 ? 'none' : '1px solid'}
                                        borderColor="gray.100"
                                        _hover={{ bg: 'gray.50' }}
                                    >
                                        <Box minW={0}>
                                            <HStack spacing={2} mb={1}>
                                                <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                                                    {timezoneDisplayLabel(r.timezone)}
                                                    {/* available_tz: 0 means this row was the "No Timezone" bucket
                                                        even when, like here, it was later assigned a real zone —
                                                        so the flag still needs to read even after that assignment. */}
                                                    {r.availableTz === 0 && (
                                                        <Text as="span" fontWeight="400" color="gray.500">
                                                            {' '}(No Timezone)
                                                        </Text>
                                                    )}
                                                </Text>
                                                {/* {i === 0 && rows.length > 1 && <Chip tone="purple">First wave</Chip>} */}
                                            </HStack>
                                            <HStack spacing={2} flexWrap="wrap">
                                                {r.timezone === NO_TIMEZONE ? (
                                                    <Chip>Sent as entered</Chip>
                                                ) : (
                                                    <>
                                                        <Chip>{zoneOffsetLabel(r.timezone)}</Chip>
                                                        <Chip>now {nowInZone(r.timezone).time}</Chip>
                                                    </>
                                                )}
                                                {r.count != null && <Chip icon={LuUsers}>{Number(r.count).toLocaleString()}</Chip>}
                                                {r.status && <Chip tone={statusTone(r.status)}>{r.status}</Chip>}
                                            </HStack>
                                        </Box>
                                        <Box textAlign="right" flexShrink={0}>
                                            {r.valid && r.instant ? (
                                                <>
                                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                                        {formatInZone(r.instant, r.timezone)}
                                                    </Text>
                                                    <Text fontSize="11px" color="gray.500">
                                                        {formatInZone(r.instant, 'UTC')} UTC
                                                    </Text>
                                                </>
                                            ) : (
                                                <Chip tone="red">{r.reason || 'No time set'}</Chip>
                                            )}
                                        </Box>
                                    </Flex>
                                ))}
                            </VStack>
                        )
                    )}

                    {canEdit && isTimezoneBased && invalidCount > 0 && (
                        <Text fontSize="xs" color="red.500" mt={3}>
                            {invalidCount === 1
                                ? '1 timezone would already have passed'
                                : `${invalidCount} timezones would already have passed`} — pick a later time.
                        </Text>
                    )}

                    {!isTimezoneBased && !canEdit && (
                        <VStack align="start" spacing={2}>
                            <Text fontSize="sm" color="gray.700">
                                This campaign sends at one instant for the whole audience.
                            </Text>
                            <HStack spacing={2}>
                                <Chip tone="blue" icon={LuClock}>
                                    {campaign?.scheduled_at
                                        ? formatInZone(new Date(campaign.scheduled_at), baseZone)
                                        : 'No schedule set'}
                                </Chip>
                                <Chip>{baseZone}</Chip>
                            </HStack>
                        </VStack>
                    )}
                </ModalBody>

                <Divider />
                <ModalFooter py={3}>
                    <Button size="sm" variant="ghost" mr={3} onClick={onClose}>
                        {canEdit ? 'Cancel' : 'Close'}
                    </Button>
                    {canEdit && (
                        <Button
                            size="sm"
                            bg="var(--brand-primary)"
                            color="white"
                            _hover={{ bg: 'var(--brand-primary-active)' }}
                            _disabled={{ bg: 'gray.400' }}
                            onClick={handleSave}
                            isLoading={saving}
                            loadingText="Rescheduling…"
                            isDisabled={blocked}
                        >
                            Reschedule
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CampaignScheduleModal;
